import { useEffect, useState, type KeyboardEvent, type SyntheticEvent } from "react";
import { ArrowRight, Copy, Save } from "lucide-react";
import { toast } from "react-hot-toast";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { RenderAssetListItem } from "@/lib/api-types";
import {
  getAssetStorefrontLabel,
  getAssetStorefrontToneClass,
  normalizeAssetStorefrontMode,
  resolveAssetStorefrontSummaryMode,
  type AssetStorefrontMode,
} from "@/lib/storefront";
import {
  buildClothingShopHash,
  formatRenderCollectionLabel,
  renderAssetPriceLabel,
} from "@/lib/render-catalog";

type RowDraft = {
  priceInput: string;
  priceType: AssetStorefrontMode;
};

function priceToneClass(asset: RenderAssetListItem): string {
  const summary = asset.pricingSummary;
  if (!summary) {
    return "border-border bg-muted text-muted-foreground";
  }
  const price = summary.defaultPrice ?? summary.maxPrice ?? summary.minPrice;
  if (price === undefined) {
    return "border-border bg-muted text-muted-foreground";
  }
  if (price <= 0) {
    return "border-amber-500/30 bg-amber-500/10 text-amber-700";
  }
  return "border-emerald-500/30 bg-emerald-500/10 text-emerald-700";
}

function resolveDraftPrice(asset: RenderAssetListItem): string {
  const summary = asset.pricingSummary;
  const price = summary?.defaultPrice ?? summary?.maxPrice ?? summary?.minPrice;
  return price === undefined ? "" : String(price);
}

function resolveDraftPriceType(asset: RenderAssetListItem): AssetStorefrontMode {
  const summaryMode = resolveAssetStorefrontSummaryMode(asset.pricingSummary);
  if (summaryMode === "money" || summaryMode === "coin" || summaryMode === "hidden") {
    return summaryMode;
  }
  return normalizeAssetStorefrontMode(
    asset.pricingSummary?.uniformPriceType ?? asset.pricingSummary?.defaultPriceType,
  );
}

function stopRowSelection(event: SyntheticEvent) {
  event.stopPropagation();
}

async function copyTextToClipboard(value: string): Promise<void> {
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }

  if (typeof document === "undefined") {
    throw new Error("Clipboard API is unavailable");
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();

  const successful = document.execCommand("copy");
  document.body.removeChild(textarea);

  if (!successful) {
    throw new Error("Không thể copy vào clipboard");
  }
}

export default function RenderCatalogAssetTable({
  assets,
  selectedAssetID,
  onSelect,
  isAdmin,
  savingAssetID,
  onSavePrice,
}: {
  assets: RenderAssetListItem[];
  selectedAssetID: string;
  onSelect: (asset: RenderAssetListItem) => void;
  isAdmin: boolean;
  savingAssetID?: string | null;
  onSavePrice?: (payload: {
    asset: RenderAssetListItem;
    priceInput: string;
    priceType: AssetStorefrontMode;
  }) => void;
}) {
  const [drafts, setDrafts] = useState<Record<string, RowDraft>>({});

  useEffect(() => {
    setDrafts(
      assets.reduce<Record<string, RowDraft>>((accumulator, asset) => {
        accumulator[asset.id] = {
          priceInput: resolveDraftPrice(asset),
          priceType: resolveDraftPriceType(asset),
        };
        return accumulator;
      }, {}),
    );
  }, [assets]);

  const submitDraft = (asset: RenderAssetListItem) => {
    if (!isAdmin || !onSavePrice) {
      return;
    }

    const draft = drafts[asset.id] ?? {
      priceInput: resolveDraftPrice(asset),
      priceType: resolveDraftPriceType(asset),
    };

    onSavePrice({
      asset,
      priceInput: draft.priceInput,
      priceType: draft.priceType,
    });
  };

  const handleDraftKeyDown = (
    event: KeyboardEvent<HTMLElement>,
    asset: RenderAssetListItem,
    draftChanged: boolean,
  ) => {
    event.stopPropagation();
    if (
      event.key !== "Enter" ||
      !draftChanged ||
      !isAdmin ||
      !onSavePrice ||
      savingAssetID
    ) {
      return;
    }

    event.preventDefault();
    submitDraft(asset);
  };

  return (
    <div className="overflow-x-auto rounded-2xl border border-border/80">
      <table className="min-w-[980px] w-full text-sm">
        <thead className="bg-muted/30 text-left text-[11px] uppercase tracking-[0.16em] text-muted-foreground">
          <tr>
            <th className="px-3 py-3 font-medium">Preview</th>
            <th className="px-3 py-3 font-medium">Asset</th>
            <th className="px-3 py-3 font-medium">Price</th>
            <th className="px-3 py-3 font-medium">Storefront</th>
            <th className="px-3 py-3 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {assets.map((asset) => {
            const selected = selectedAssetID === asset.id;
            const storefrontSummaryMode = resolveAssetStorefrontSummaryMode(
              asset.pricingSummary,
            );
            const linkedTextureCount =
              asset.pricingSummary?.linkedTextureCount ?? 0;
            const drawableMeshPartCount = Math.max(
              asset.drawableMeshPartCount ?? 1,
              1,
            );
            const draft = drafts[asset.id] ?? {
              priceInput: resolveDraftPrice(asset),
              priceType: resolveDraftPriceType(asset),
            };
            const initialPriceInput = resolveDraftPrice(asset);
            const initialPriceType = resolveDraftPriceType(asset);
            const draftChanged =
              draft.priceInput !== initialPriceInput ||
              draft.priceType !== initialPriceType;
            const rowSaving = savingAssetID === asset.id;
            const clothingShopHash = buildClothingShopHash(
              asset,
              asset.defaultTextureIndex,
            );

            return (
              <tr
                key={asset.id}
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                onClick={() => onSelect(asset)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect(asset);
                  }
                }}
                className={`cursor-pointer border-t border-border/80 align-top transition-colors first:border-t-0 ${
                  selected
                    ? "bg-primary/10"
                    : "bg-card/50 hover:bg-muted/30"
                }`}
              >
                <td className="px-3 py-3">
                  <div className="h-16 w-14 overflow-hidden rounded-xl border border-border/80 bg-muted/20">
                    {asset.previewImageUrl ? (
                      <img
                        src={asset.previewImageUrl}
                        alt={`Preview ${asset.assetName}`}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                        N/A
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="space-y-1.5">
                    <div className="flex max-w-[320px] flex-wrap items-center gap-2">
                      <p className="max-w-[260px] truncate font-medium">
                        {asset.assetName}
                      </p>
                      {drawableMeshPartCount > 1 ? (
                        <span className="rounded-full border border-border/80 px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                          {drawableMeshPartCount} mesh parts
                        </span>
                      ) : null}
                    </div>
                    <p className="max-w-[280px] truncate text-xs text-muted-foreground">
                      {formatRenderCollectionLabel(asset.collectionDisplay)} • {asset.pedGender}
                    </p>
                    <div
                      className="flex max-w-[320px] items-center gap-1 text-xs text-muted-foreground"
                      onClick={stopRowSelection}
                      onKeyDown={stopRowSelection}
                    >
                      <p className="truncate font-mono">{clothingShopHash}</p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0"
                        title="Copy clothing hash"
                        aria-label={`Copy clothing hash ${clothingShopHash}`}
                        onClick={() => {
                          copyTextToClipboard(clothingShopHash)
                            .then(() => {
                              toast.success("Đã copy clothing hash");
                            })
                            .catch((error: unknown) => {
                              toast.error(
                                error instanceof Error
                                  ? error.message
                                  : "Không thể copy clothing hash",
                              );
                            });
                        }}
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <p className="max-w-[280px] truncate text-xs text-muted-foreground">
                      {asset.textureCount} textures • {linkedTextureCount} linked • {asset.ddsCount} DDS
                    </p>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="space-y-2">
                    <span
                      className={`inline-flex rounded-md border px-2 py-1 text-xs font-semibold ${priceToneClass(asset)}`}
                    >
                      {renderAssetPriceLabel(asset.pricingSummary)}
                    </span>
                    {isAdmin ? (
                      <div onClick={stopRowSelection} onKeyDown={stopRowSelection}>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          inputMode="numeric"
                          value={draft.priceInput}
                          onChange={(event) =>
                            setDrafts((current) => ({
                              ...current,
                              [asset.id]: {
                                ...draft,
                                priceInput: event.target.value,
                              },
                            }))
                          }
                          onKeyDown={(event) =>
                            handleDraftKeyDown(event, asset, draftChanged)
                          }
                          className="h-9 w-28"
                          placeholder="0"
                        />
                      </div>
                    ) : null}
                    {isAdmin ? (
                      <p className="text-xs text-muted-foreground">
                        Save sẽ áp cho tất cả texture linked.
                      </p>
                    ) : null}
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="space-y-2">
                    <span
                      className={`inline-flex rounded-md border px-2 py-1 text-xs font-semibold ${getAssetStorefrontToneClass(
                        storefrontSummaryMode,
                      )}`}
                    >
                      {getAssetStorefrontLabel(storefrontSummaryMode)}
                    </span>
                    {isAdmin ? (
                      <div onClick={stopRowSelection} onKeyDown={stopRowSelection}>
                        <select
                          value={draft.priceType}
                          onChange={(event) =>
                            setDrafts((current) => ({
                              ...current,
                              [asset.id]: {
                                ...draft,
                                priceType: normalizeAssetStorefrontMode(
                                  event.target.value,
                                ),
                              },
                            }))
                          }
                          onKeyDown={(event) =>
                            handleDraftKeyDown(event, asset, draftChanged)
                          }
                          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                        >
                          <option value="money">Tiền ingame</option>
                          <option value="coin">Coin</option>
                          <option value="hidden">Hidden</option>
                        </select>
                      </div>
                    ) : null}
                  </div>
                </td>
                <td className="px-3 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    {isAdmin ? (
                      <div onClick={stopRowSelection} onKeyDown={stopRowSelection}>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => submitDraft(asset)}
                          disabled={!draftChanged || Boolean(savingAssetID)}
                        >
                          <Save className="mr-2 h-4 w-4" />
                          {rowSaving ? "Saving..." : "Save all"}
                        </Button>
                      </div>
                    ) : null}
                    <ArrowRight className="ml-auto h-4 w-4 text-muted-foreground" />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
